import { useContext } from "react";

import { useQuery } from "@tanstack/react-query";
import {
  CaseListResult,
  CaseOutcomeResponse,
  CaseResponse,
  DenialTypes,
  PrescriberPracticeLocationResponse,
  PriorAuthorizationGetResponse,
} from "capitalrx-types";

import { AuthenticationContext } from "../context";
import { MemberEligibilityContext } from "../context/MemberEligibilityContext";
import { ANALYTIC_EVENTS, logEvent } from "../service/analyticsService";
import { MemberEligibilityInfo } from "../types";
import { PresignedDownloadUrl, PriorAuthorizationDocumentURLRequest } from "../types/documents";
import { getEligibilityBasedQueryKey, getViewRequest, request } from "./request";

export interface PriorAuthorizationCaseGetRequest {
  readonly id: number;
}

interface MemberAppCaseResponse extends CaseResponse {
  readonly renewal_start_date?: string;
  readonly case_outcome?: Omit<CaseOutcomeResponse, "outcome"> & {
    outcome: "approved" | "denied" | "closed";
  };
  readonly prescriber_practice_location?: PrescriberPracticeLocationResponse | null;
  readonly prescriber_first_name: string;
  readonly prescriber_last_name: string;
  readonly prescriber_business_practice_phone: string;
  readonly prior_authorization: PriorAuthorizationGetResponse;
  readonly denial_type: DenialTypes | null;
  readonly denial_reason: string | null;
}
export const getPriorAuthorizationCase = async (authToken: string, body: PriorAuthorizationCaseGetRequest) => {
  return request<PriorAuthorizationCaseGetRequest, MemberAppCaseResponse>(
    {
      endpoint: "/prior_authorization_case/get",
      body: { ...body },
    },
    authToken,
  );
};

export const listPriorAuthorizationCase = async (authToken: string, body: PriorAuthorizationCaseListRequest) => {
  return request<PriorAuthorizationCaseListRequest, PriorAuthorizationCaseListResponse>(
    {
      endpoint: "/prior_authorization_case/list",
      body: { ...body },
    },
    authToken,
  );
};

export const useGetPriorAuthorizationCaseQuery = (body: PriorAuthorizationCaseGetRequest) => {
  const { isViewingDataholder, memberEligibility } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);
  const caseId = body.id;
  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return getPriorAuthorizationCase(await getAuthToken(), {
          ...body,
          ...getViewRequest(memberEligibility),
        });
      } else {
        return getPriorAuthorizationCase(await getAuthToken(), { ...body });
      }
    },
    queryKey: ["priorAuthorizationGet", caseId, ...getEligibilityBasedQueryKey(memberEligibility)],
  });
  return query;
};

export const listPriorAuthorizationDecisionLetter = (authToken: string, body: { case_id: number }) => {
  return request<{ case_id: number }, PriorAuthorizationDecisionLetterListResponse>(
    {
      endpoint: "/prior_authorization_case/list_documents",
      body,
    },
    authToken,
  );
};

export const useListPriorAuthorizationDecisionLetter = (body: { case_id: number }) => {
  const { isViewingDataholder, memberEligibility } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);
  return useQuery<PriorAuthorizationDecisionLetterListResponse>({
    queryFn: async () => {
      return listPriorAuthorizationDecisionLetter(await getAuthToken(), {
        case_id: body.case_id,
        ...(isViewingDataholder ? getViewRequest(memberEligibility) : {}),
      });
    },
    queryKey: ["priorAuthorizationLetterList", ...getEligibilityBasedQueryKey(memberEligibility)],
  });
};

export const useListPriorAuthorizationCaseQuery = (body: PriorAuthorizationCaseListRequest) => {
  const { isViewingDataholder, memberEligibility } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);
  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return listPriorAuthorizationCase(await getAuthToken(), {
          ...body,
          ...getViewRequest(memberEligibility),
        });
      } else {
        return listPriorAuthorizationCase(await getAuthToken(), { ...body });
      }
    },
    queryKey: ["priorAuthorizationList", body?.page_number || 1, ...getEligibilityBasedQueryKey(memberEligibility)],
  });

  return query;
};

export const getPriorAuthorizationDocumentsUrl = async (
  authToken: string,
  data: PriorAuthorizationDocumentURLRequest,
  memberEligibility: MemberEligibilityInfo,
  isViewingDataholder: boolean,
) => {
  try {
    const response = (await request<PriorAuthorizationDocumentURLRequest, PresignedDownloadUrl>(
      {
        endpoint: "/prior_authorization_case/letter_url_for_download",
        body: {
          ...data,
          ...(isViewingDataholder ? getViewRequest(memberEligibility) : {}),
        },
      },
      authToken,
    )) as PresignedDownloadUrl;
    return response;
  } catch (err) {
    logEvent(ANALYTIC_EVENTS.ERRORS.getPriorAuthorizationDocumentsUrl);
    throw err;
  }
};

export interface PriorAuthorizationGetRequest {
  readonly id: string;
}

export interface PriorAuthorizationCaseListRequest {
  readonly case_outcome?: string;
  readonly text_search?: string;
  readonly page_number?: number;
  readonly results_per_page?: number;
}

export interface PriorAuthorizationCaseListResult extends CaseListResult {
  readonly prior_authorization?: PriorAuthorizationGetResponse;
}

export interface PriorAuthorizationCaseListResponse {
  readonly result_count: string;
  readonly results: ReadonlyArray<PriorAuthorizationCaseListResult>;
}

export interface PriorAuthorizationDecisionLetterListResponse {
  readonly result_count: string;
  readonly results: ReadonlyArray<PriorAuthorizationDecisionLetterListResult>;
}

export interface PriorAuthorizationDecisionLetterListResult {
  readonly letter: PriorAuthorizationDecisionSubLetter;
}

export interface PriorAuthorizationDecisionSubLetter {
  readonly case_id: number;
  readonly created_at: string;
  readonly filename: string;
  readonly language?: string;
  readonly uuid: string;
}
